import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import axios from 'axios';
import { tagText } from '../lib/assets';
import { fmtDate, fmtDateTime } from '../lib/when';
import { updatesLine } from '../components/asset/assetView';
import '../components/Assets.css';

/**
 * Fleet health (Gordon, 30 October): device health only, never per person.
 *
 *   Patching      lib/device-power.js's one rule, each with its reasons:
 *                 Windows Update behind (agent 0.5.7) or, without its
 *                 answer, not restarted in 14 days or waiting on a restart
 *   Not used      laptops with an agent that have not checked in for 30 days
 *   In the office how many laptops checked in from an office network each
 *                 day - numbers only, no names
 *   Health        (agent 0.5.6) crashes and unexpected shutdowns in 30 days
 *   Left on       (agent 0.5.6) awake overnight on 3 or more of 14 nights
 */
const API_BASE = process.env.REACT_APP_API_URL || 'http://localhost:5000/api';
const WEEKDAYS = ['Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat', 'Sun'];
const daysSince = (t) => (t ? Math.floor((Date.now() - new Date(t).getTime()) / 86400000) : null);

function Device({ d }) {
  return (
    <>
      <Link to={`/assets/${d.id}/device`}>{tagText(d)}</Link>
      <span className="as-quiet">{` · ${[d.manufacturer, d.model].filter(Boolean).join(' ') || d.category_name}${d.holder_name ? ` · ${d.holder_name}` : ''}`}</span>
    </>
  );
}

function Section({ id, title, why, rows, empty, children }) {
  return (
    <section className="as-panel" aria-labelledby={id} data-testid={id} style={{ marginBottom: '1rem' }}>
      <h2 id={id}>{`${title}${rows ? ` (${rows.length})` : ''}`}</h2>
      <p className="as-quiet">{why}</p>
      {rows && !rows.length ? <p className="as-quiet">{empty}</p> : children}
    </section>
  );
}

export default function AssetFleet() {
  const [data, setData] = useState(null);
  const [error, setError] = useState('');
  useEffect(() => {
    axios.get(`${API_BASE}/assets/devices/fleet`).then((r) => setData(r.data))
      .catch((e) => setError(e?.response?.data?.error || 'Could not load fleet health.'));
  }, []);

  if (error) return <div className="container"><p><Link to="/assets/devices">← Devices</Link></p><p className="as-note" data-tone="warning" role="alert">{error}</p></div>;
  if (!data) return <div className="container"><p className="as-quiet">Loading…</p></div>;
  const t = data.thresholds;
  const office = data.office || [];
  const peak = Math.max(1, ...office.map((d) => d.devices));
  // Average per weekday over the weeks shown, weekdays only where seen.
  const byDay = WEEKDAYS.map((w, i) => {
    const days = office.filter((d) => ((new Date(`${d.day}T12:00:00Z`).getUTCDay() + 6) % 7) === i);
    return { w, avg: days.length ? Math.round((days.reduce((s, d) => s + d.devices, 0) / days.length) * 10) / 10 : null };
  });

  return (
    <div className="container">
      <p><Link to="/assets/devices">← Devices</Link></p>
      <div className="page-header">
        <h1 className="page-title">Fleet health</h1>
        <p className="page-description">
          How the firm’s computers are doing - patches, machines nobody uses, crashes - and how many were in the
          office each day. About the devices, never about the people using them.
        </p>
      </div>

      <Section id="fleet-patching" title="Needs patching" rows={data.patching}
        why={`Heard from in the last ${t.patch_days} days and waiting for a restart, with a security update waiting more than ${t.security_grace_days ?? 7} days, or with Windows Update not checking for ${t.search_days ?? 14} days or not installing for ${t.install_days ?? 30}. A computer that does not report Windows Update (a Mac, an agent before 0.5.7) is listed when it has not restarted in ${t.patch_days} days.`}
        empty="Every reporting computer is up to date.">
        <ul className="as-tree">
          {data.patching.map((d) => (
            <li key={d.id} title={updatesLine(d.updates) || undefined}>
              <Device d={d} />
              <span className="as-spacer" />
              {(d.reasons || []).map((r) => <span key={r} className="as-chip" data-tone="warning">{r}</span>)}
              {d.last_boot && <span className="as-quiet">{`Restarted ${daysSince(d.last_boot)} days ago`}</span>}
            </li>
          ))}
        </ul>
      </Section>

      <Section id="fleet-unused" title="Not switched on" rows={data.unused}
        why={`A Jarvis agent, but no check-in for ${t.unused_days} days: in a drawer, with a leaver, or broken - worth reassigning or returning.`}
        empty="Every computer with an agent has checked in this month.">
        <ul className="as-tree">
          {data.unused.map((d) => (
            <li key={d.id}>
              <Device d={d} />
              <span className="as-spacer" />
              <span className="as-quiet">{d.last_heartbeat_at ? `last seen ${fmtDate(d.last_heartbeat_at)}` : 'never checked in'}</span>
            </li>
          ))}
        </ul>
      </Section>

      <Section id="fleet-office" title="In the office"
        why={`How many computers checked in from an office network each day, over the last ${t.office_days} days. Numbers only - no names.`}>
        {!office.length ? <p className="as-quiet">No office check-ins yet. Laptops need location reporting on and a known office network.</p> : (
          <>
            <div className="fleet-bars" data-testid="fleet-office-bars" style={{ display: 'flex', alignItems: 'flex-end', gap: 2, height: 120, overflowX: 'auto' }}>
              {office.map((d) => (
                <div key={d.day} title={`${fmtDate(d.day)}: ${d.devices}`}
                  style={{ flex: '1 0 10px', minWidth: 10, height: `${Math.max(4, (d.devices / peak) * 100)}%`, background: 'var(--color-primary)', borderRadius: '3px 3px 0 0' }} />
              ))}
            </div>
            <p className="as-quiet" data-testid="fleet-weekdays">
              {'Average by weekday: '}
              {byDay.filter((d) => d.avg != null).map((d) => `${d.w} ${d.avg}`).join(' · ')}
            </p>
          </>
        )}
      </Section>

      {!data.power ? (
        <p className="as-note" data-tone="info">
          Crashes, unexpected shutdowns and machines left on overnight appear here once migration 116 has run and
          laptops are on agent 0.5.6.
        </p>
      ) : (
        <>
          <Section id="fleet-health" title="Crashes and unexpected shutdowns" rows={data.power.health}
            why={`In the last ${t.health_days} days, from Windows’ own log. Repeated ones often mean a failing battery, overheating or a driver.`}
            empty="None in the last 30 days.">
            <ul className="as-tree">
              {data.power.health.map((d) => (
                <li key={d.id}>
                  <Device d={d} />
                  <span className="as-spacer" />
                  {d.crashes > 0 && <span className="as-chip" data-tone="danger">{`${d.crashes} blue screen${d.crashes === 1 ? '' : 's'}`}</span>}
                  {d.unexpected > 0 && <span className="as-chip" data-tone="warning">{`${d.unexpected} unexpected shutdown${d.unexpected === 1 ? '' : 's'}`}</span>}
                  <span className="as-quiet">{`last ${fmtDateTime(d.last_at)}`}</span>
                </li>
              ))}
            </ul>
          </Section>
          <Section id="fleet-overnight" title="Left on overnight" rows={data.power.overnight}
            why="Awake for more than an hour between 22:00 and 06:00 on 3 or more of the last 14 nights - energy, and a laptop that never sleeps."
            empty="No computer is regularly left on at night.">
            <ul className="as-tree">
              {data.power.overnight.map((d) => (
                <li key={d.id}>
                  <Device d={d} />
                  <span className="as-spacer" />
                  <span className="as-quiet">{`${d.awake_nights} of ${d.nights} nights`}</span>
                </li>
              ))}
            </ul>
          </Section>
        </>
      )}
    </div>
  );
}
